import React, { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { get, patch, post } from "../lib/api";
import { useDocumentTitle, useLoad } from "../lib/hooks";
import { addDays, channelLabel, dueText, formatDay, humanize, optionLabel, plural } from "../lib/format";
import { formatEUR, rangeText, SIGNAL_TYPES, weekStartOf } from "../lib/reports";
import { todayISO } from "../../helpers/datetime";
import { Badge, Button, Card, Checkbox, Dot, Empty, ErrorBox, Input, Loading, PageHead, Spinner, TierBadge, dueDotColor } from "../ui";
import TargetCard from "../components/TargetCard";
import { useShell } from "../shell/context";

const entityPath = (kind, id) => (kind === "company" ? `/crm/companies/${id}` : `/crm/contacts/${id}`);
const roleText = (r) => (r ? [r.title, r.company_name].filter(Boolean).join(" at ") : "");
const signalType = (t) => optionLabel(SIGNAL_TYPES, t);

function useWeek() {
    const location = useLocation();
    const history = useHistory();
    const param = new URLSearchParams(location.search).get("week");
    const thisWeek = weekStartOf(todayISO());
    const week = weekStartOf(param || "") || thisWeek;
    const setWeek = (w) => {
        const ws = weekStartOf(w);
        if (!ws) return;
        history.replace({ search: ws === thisWeek ? "" : `?week=${ws}` });
    };
    return [week, setWeek, thisWeek];
}

// ---------- sections ----------

function LastWeek({ lw }) {
    const channels = Object.entries(lw.touches_by_channel || {}).filter(([, n]) => n > 0);
    const tiles = [
        { label: "Touches", value: lw.touches },
        { label: "Introductions", value: lw.introductions },
        { label: "Conversations", value: lw.conversations },
        { label: "Proposals", value: lw.proposals },
        { label: "Closes", value: lw.closes, sub: lw.closes ? `${lw.won} won · ${lw.lost} lost` : null },
        { label: "Contracted", value: formatEUR(lw.contracted_value_eur) },
    ];
    return (
        <Card title="Last week's numbers" actions={<span className="hz-small hz-muted">{rangeText(lw.from, lw.to)}</span>}>
            <div className="hz-tiles">
                {tiles.map((t) => (
                    <div key={t.label} className="hz-tile">
                        <div className="hz-stat">{t.value}</div>
                        <div className="hz-stat-label">{t.label}</div>
                        {t.sub && <div className="hz-xs hz-muted">{t.sub}</div>}
                    </div>
                ))}
            </div>
            {channels.length > 0 && (
                <div className="hz-small hz-muted" style={{ marginTop: 12 }}>
                    {channels.map(([c, n]) => `${channelLabel(c)} ${n}`).join(" · ")}
                </div>
            )}
            {(lw.closed_leads || []).length > 0 && (
                <div style={{ marginTop: 12, borderTop: "1px solid var(--hz-divider-soft)" }}>
                    {lw.closed_leads.map((l) => (
                        <div key={l.id} className="hz-row" style={{ padding: "8px 0", borderBottom: "1px solid var(--hz-divider-soft)" }}>
                            <Badge color={l.stage === "won" ? "success" : "secondary"}>{l.stage === "won" ? "Won" : "Lost"}</Badge>
                            <Link to={`/crm/leads/${l.id}`} className="hz-spacer hz-ellipsis">
                                {l.title || l.company_name}
                                {l.title && <span className="hz-muted"> · {l.company_name}</span>}
                            </Link>
                            <span className="hz-small hz-muted">{humanize(l.source)}</span>
                            <span className="hz-strong">{l.value_eur ? formatEUR(l.value_eur) : ""}</span>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

function DueSection({ due, onDone }) {
    const { openLog } = useShell();
    const rows = due.data || [];
    return (
        <Card
            noBody
            title="Who to touch this week"
            actions={
                <span className="hz-small hz-muted">
                    {due.counts.overdue} overdue · {due.counts.due_soon} due this week · A tier first
                </span>
            }
        >
            {rows.length === 0 && <Empty>Nobody due this week.</Empty>}
            {rows.map((r) => {
                const isContact = r.kind === "contact";
                return (
                    <div key={r.kind + r.id} className="hz-list-item hz-brief-item" style={{ alignItems: "flex-start" }}>
                        <span style={{ paddingTop: 5 }}>
                            <Dot color={dueDotColor(r.due_status)} />
                        </span>
                        <div className="hz-spacer" style={{ minWidth: 0 }}>
                            <div className="hz-row-wrap" style={{ gap: 8 }}>
                                <Link to={entityPath(r.kind, r.id)} className="hz-strong hz-text-body">{r.name}</Link>
                                {isContact ? <TierBadge tier={r.tier} custom={r.cadence_source === "custom"} /> : <Badge color="secondary">Company</Badge>}
                                {r.current_role && <span className="hz-small hz-muted">{roleText(r.current_role)}</span>}
                                <span className={`hz-small hz-status-${dueDotColor(r.due_status)}`}>{dueText(r.due_in_days)}</span>
                            </div>
                            <div className="hz-small hz-muted" style={{ marginTop: 2 }}>
                                {r.last_touch
                                    ? `Last touch: ${channelLabel(r.last_touch.channel)} · ${formatDay(r.last_touch.touch_date, { short: true })}${r.last_touch.summary ? ` · ${r.last_touch.summary}` : ""}`
                                    : "No touch logged yet."}
                            </div>
                            {r.reason && r.reason.kind !== "never_touched" && (
                                <div className="hz-small" style={{ color: "var(--hz-gray-700)", marginTop: 2 }}>
                                    <i className="ri-lightbulb-line" style={{ marginRight: 4 }}></i>
                                    {r.reason.text}
                                </div>
                            )}
                            {r.open_follow_up && (
                                <div className="hz-small hz-row-wrap" style={{ marginTop: 2, color: "var(--hz-warning-text)", gap: 6 }}>
                                    <span>
                                        <i className="ri-flag-line"></i> {r.open_follow_up.follow_up || "Follow-up"}
                                        {r.open_follow_up.follow_up_date ? ` · ${formatDay(r.open_follow_up.follow_up_date, { short: true })}` : ""}
                                    </span>
                                    {r.open_follow_up.touch_id && (
                                        <button type="button" className="hz-btn-link hz-no-print" onClick={() => onDone(r.open_follow_up.touch_id)}>
                                            Mark done
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <Button
                            size="sm"
                            variant="soft"
                            icon="ri-chat-new-line"
                            className="hz-no-print"
                            onClick={() => openLog(isContact ? { contact: { id: r.id, name: r.name, current_role: r.current_role } } : { company: { id: r.id, name: r.name } })}
                        >
                            Log touch
                        </Button>
                    </div>
                );
            })}
            {due.count > rows.length && <div className="hz-empty-sm">{due.count - rows.length} more not shown.</div>}
        </Card>
    );
}

function FollowUpSection({ followUps, weekEnd, onDone }) {
    const { openLog } = useShell();
    const today = todayISO();
    const rows = followUps.data || [];
    return (
        <Card noBody title="Follow-ups due" actions={<span className="hz-small hz-muted">Promised in past touches, due by {formatDay(weekEnd, { short: true })}</span>}>
            {rows.length === 0 && <Empty>No follow-ups due.</Empty>}
            {rows.map((t) => {
                const overdue = t.follow_up_date && t.follow_up_date < today;
                const who = t.contact_id ? { kind: "contact", id: t.contact_id, name: t.contact_name } : { kind: "company", id: t.company_id, name: t.company_name };
                return (
                    <div key={t.id} className="hz-list-item hz-brief-item">
                        <i className="ri-flag-line" style={{ color: overdue ? "var(--hz-danger)" : "var(--hz-warning-text)", fontSize: 16 }}></i>
                        <div className="hz-spacer" style={{ minWidth: 0 }}>
                            <div className="hz-strong">{t.follow_up || "Follow-up"}</div>
                            <div className="hz-small hz-muted">
                                <Link to={entityPath(who.kind, who.id)}>{who.name}</Link>
                                {t.contact_id && t.company_name ? ` · ${t.company_name}` : ""}
                                {" · "}
                                <span className={overdue ? "hz-status-danger" : ""}>{formatDay(t.follow_up_date, { short: true })}</span>
                                {` · from ${channelLabel(t.channel).toLowerCase()} on ${formatDay(t.touch_date, { short: true })}`}
                            </div>
                        </div>
                        <div className="hz-row hz-no-print" style={{ gap: 6 }}>
                            <Button
                                size="sm"
                                variant="outline"
                                icon="ri-chat-new-line"
                                title="Log a touch"
                                onClick={() => openLog(t.contact_id ? { contact: { id: t.contact_id, name: t.contact_name } } : { company: { id: t.company_id, name: t.company_name } })}
                            >
                                Log
                            </Button>
                            <Button size="sm" variant="soft-success" icon="ri-check-line" onClick={() => onDone(t.id)}>Done</Button>
                        </div>
                    </div>
                );
            })}
            {followUps.count > rows.length && <div className="hz-empty-sm">{followUps.count - rows.length} more not shown.</div>}
        </Card>
    );
}

function NextStepForm({ lead, onSaved, onCancel }) {
    const { toast } = useShell();
    const [step, setStep] = useState(lead.next_step || "");
    const [date, setDate] = useState(lead.next_step_date && lead.next_step_date >= todayISO() ? lead.next_step_date : addDays(todayISO(), 7));
    const [busy, setBusy] = useState(false);
    const save = async (e) => {
        e.preventDefault();
        if (!step.trim() || !date) return toast("A next step needs a text and a date.", { error: true });
        setBusy(true);
        try {
            await patch(`v1/crm/leads/${lead.id}`, { next_step: step.trim(), next_step_date: date });
            toast("Next step saved.");
            onSaved();
        } catch (err) {
            toast(err.message, { error: true });
            setBusy(false);
        }
    };
    return (
        <form className="hz-row-wrap" style={{ gap: 6, marginTop: 6 }} onSubmit={save}>
            <Input small autoFocus value={step} onChange={(e) => setStep(e.target.value)} placeholder="Next step" style={{ flex: "1 1 200px", width: "auto" }} />
            <Input small type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 140 }} />
            <Button size="sm" variant="primary" type="submit" disabled={busy}>{busy && <Spinner />}Save</Button>
            <Button size="sm" variant="light" onClick={onCancel}>Cancel</Button>
        </form>
    );
}

function LeadRow({ lead, onChanged }) {
    const [editing, setEditing] = useState(false);
    return (
        <div className="hz-list-item hz-brief-item" style={{ alignItems: "flex-start" }}>
            <i className="ri-funds-line" style={{ fontSize: 16, color: "var(--hz-primary)", marginTop: 2 }}></i>
            <div className="hz-spacer" style={{ minWidth: 0 }}>
                <div className="hz-row-wrap" style={{ gap: 6 }}>
                    <Link to={`/crm/leads/${lead.id}`} className="hz-strong hz-text-body">{lead.title || lead.company_name}</Link>
                    {lead.title && <Link to={`/crm/companies/${lead.company_id}`} className="hz-small">{lead.company_name}</Link>}
                    <Badge color="info">{humanize(lead.stage)}</Badge>
                    {lead.stall_status === "stalled" && <Badge color="danger" soft={false}>Stalled</Badge>}
                    {lead.stall_status === "no_next_step" && <Badge color="warning" soft={false}>No next step</Badge>}
                </div>
                <div className="hz-small hz-muted">
                    {lead.next_step ? `Next: ${lead.next_step}${lead.next_step_date ? " · " + formatDay(lead.next_step_date, { short: true }) : ""}` : "No next step"}
                    {lead.days_in_stage !== null && lead.days_in_stage !== undefined ? ` · ${lead.days_in_stage}d in stage` : ""}
                    {lead.primary_contact_name ? ` · ${lead.primary_contact_name}` : ""}
                    {lead.value_eur ? ` · ${formatEUR(lead.value_eur)}` : ""}
                </div>
                {editing && <NextStepForm lead={lead} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); }} />}
            </div>
            {!editing && (
                <div className="hz-row hz-no-print" style={{ gap: 6 }}>
                    <Button size="sm" variant="soft" icon="ri-flag-2-line" onClick={() => setEditing(true)}>Set next step</Button>
                    <Link to={`/crm/leads/${lead.id}`} className="hz-btn hz-btn-sm hz-btn-outline">Open</Link>
                </div>
            )}
        </div>
    );
}

function LeadsSection({ leads, onChanged }) {
    const stalled = leads.stalled || [];
    const noStep = leads.no_next_step || [];
    return (
        <Card noBody title="Leads needing a push" actions={<span className="hz-small hz-muted">{stalled.length} stalled · {noStep.length} without a next step</span>}>
            {stalled.length + noStep.length === 0 && <Empty>No stalled leads.</Empty>}
            {stalled.length > 0 && <div className="hz-subhead">Stalled: next-step date passed</div>}
            {stalled.map((l) => <LeadRow key={l.id} lead={l} onChanged={onChanged} />)}
            {noStep.length > 0 && <div className="hz-subhead">No next step</div>}
            {noStep.map((l) => <LeadRow key={l.id} lead={l} onChanged={onChanged} />)}
        </Card>
    );
}

function SignalsSection({ signals, onChanged }) {
    const { toast } = useShell();
    const [busy, setBusy] = useState(null);
    const setActed = async (s, acted) => {
        setBusy(s.id);
        try {
            await patch(`v1/crm/signals/${s.id}`, { acted_on: acted });
            toast(acted ? "Signal marked acted on." : "Signal marked not acted on.");
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        } finally {
            setBusy(null);
        }
    };
    const groups = signals.data || [];
    return (
        <Card
            noBody
            title="New signals"
            actions={
                <span className="hz-small hz-muted">
                    Since {formatDay(signals.since, { short: true })} · {plural(signals.signals, "signal")} · {signals.not_acted_on} not acted on
                </span>
            }
        >
            {groups.length === 0 && <Empty>No new signals.</Empty>}
            {groups.map((g) => (
                <div key={g.company_id} className="hz-brief-group">
                    <div className="hz-row" style={{ padding: "10px 16px 4px" }}>
                        <i className="ri-building-line hz-muted"></i>
                        <Link to={`/crm/companies/${g.company_id}`} className="hz-strong hz-text-body hz-spacer">{g.company_name}</Link>
                        <span className="hz-small hz-muted">{plural(g.count, "signal")}</span>
                    </div>
                    {g.signals.map((s) => (
                        <div key={s.id} className="hz-row hz-brief-item" style={{ padding: "6px 16px 6px 38px", alignItems: "flex-start" }}>
                            <div className="hz-spacer" style={{ minWidth: 0 }}>
                                <div className="hz-row-wrap" style={{ gap: 6 }}>
                                    <Badge color={s.acted_on ? "secondary" : "primary"}>{signalType(s.type)}</Badge>
                                    <span className={s.acted_on ? "hz-muted" : ""}>{s.detail || "No detail"}</span>
                                </div>
                                <div className="hz-xs hz-muted" style={{ marginTop: 2 }}>
                                    Seen {formatDay(s.seen_on, { short: true })}
                                    {s.source === "claude" ? " · added by Claude" : ""}
                                    {s.source_url && (
                                        <>
                                            {" · "}
                                            <a href={s.source_url} target="_blank" rel="noopener noreferrer">Source</a>
                                        </>
                                    )}
                                    {s.lead_count ? ` · ${plural(s.lead_count, "lead")}` : ""}
                                </div>
                            </div>
                            <span className="hz-no-print">
                                <Checkbox checked={s.acted_on} disabled={busy === s.id} onChange={(v) => setActed(s, v)}>Acted on</Checkbox>
                            </span>
                            <span className="hz-print-only hz-small">{s.acted_on ? "Acted on" : "Open"}</span>
                        </div>
                    ))}
                    {g.count > g.signals.length && <div className="hz-xs hz-muted" style={{ padding: "0 16px 8px 38px" }}>{g.count - g.signals.length} more</div>}
                </div>
            ))}
        </Card>
    );
}

// ---------- page ----------

export default function WeeklyBrief() {
    useDocumentTitle("Weekly brief");
    const { version, bump, toast } = useShell();
    const [week, setWeek, thisWeek] = useWeek();
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/reports/weekly-brief", { week_start: week }), [week, version]);

    const followUpDone = async (touchId) => {
        try {
            await post(`v1/crm/touches/${touchId}/follow-up-done`);
            toast("Follow-up done.", {
                action: { label: "Undo", run: () => patch(`v1/crm/touches/${touchId}`, { follow_up_done: false }).then(bump) },
            });
            bump();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    const weekEnd = addDays(week, 6);
    const isPast = week < thisWeek;
    return (
        <>
            <PageHead
                crumb="Reports"
                title="Weekly brief"
                sub={
                    <>
                        Week of {rangeText(week, weekEnd)}
                        {data && data.as_of && ` · as of ${formatDay(data.as_of, { short: true })}`}
                    </>
                }
                actions={
                    <div className="hz-row hz-no-print" style={{ gap: 6 }}>
                        <Button variant="outline" iconOnly icon="ri-arrow-left-s-line" title="Previous week" style={{ width: 37, height: 37 }} onClick={() => setWeek(addDays(week, -7))} />
                        <Input type="date" aria-label="Week" value={week} onChange={(e) => setWeek(e.target.value)} style={{ width: 150 }} />
                        <Button variant="outline" iconOnly icon="ri-arrow-right-s-line" title="Next week" style={{ width: 37, height: 37 }} onClick={() => setWeek(addDays(week, 7))} />
                        {week !== thisWeek && <Button variant="light" onClick={() => setWeek(thisWeek)}>This week</Button>}
                        <Button variant="outline" icon="ri-printer-line" onClick={() => window.print()}>Print</Button>
                    </div>
                }
            />
            {isPast && (
                <div className="hz-alert hz-alert-info hz-no-print" style={{ marginBottom: 16 }}>
                    <i className="ri-information-line"></i> Sections other than last week's numbers show the CRM as it is now, seen from that Monday; a past brief is not a snapshot.
                </div>
            )}
            {error && <ErrorBox error={error} onRetry={reload} />}
            {loading && !data && <Card><Loading /></Card>}
            {data && (
                <div className="hz-brief">
                    <div className="hz-grid-2">
                        <LastWeek lw={data.last_week} />
                        <TargetCard target={data.quarter_target} />
                    </div>
                    <div className="hz-grid-2" style={{ marginTop: "var(--hz-gutter)" }}>
                        <div className="hz-stack">
                            <DueSection due={data.due} onDone={followUpDone} />
                            <FollowUpSection followUps={data.follow_ups} weekEnd={data.week_end} onDone={followUpDone} />
                        </div>
                        <div className="hz-stack">
                            <LeadsSection leads={data.leads} onChanged={bump} />
                            <SignalsSection signals={data.signals} onChanged={bump} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
